import { useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent } from 'react';

import { Button } from './Button';

const ACCEPT = 'image/jpeg,image/png,image/webp';

/** Moldura no ecra: o lado maior tem `frame` pixeis, o outro segue o formato. */
export function frameSize(aspect: number, frame: number): { width: number; height: number } {
  return aspect >= 1
    ? { width: frame, height: Math.round(frame / aspect) }
    : { width: Math.round(frame * aspect), height: frame };
}

type Box = { width: number; height: number };
type View = { zoom: number; x: number; y: number };

/** Escala com zoom 1: a imagem cobre a moldura toda, sem sobrar fundo. */
const baseScale = (img: Box, box: Box) => Math.max(box.width / img.width, box.height / img.height);

/** A imagem tem de cobrir a moldura toda: nunca aparece fundo vazio. */
export function clampView(img: Box, box: Box, view: View): View {
  const s = baseScale(img, box) * view.zoom;
  const minX = box.width - img.width * s;
  const minY = box.height - img.height * s;
  return {
    ...view,
    x: Math.min(0, Math.max(minX, view.x)),
    y: Math.min(0, Math.max(minY, view.y)),
  };
}

/** Comeca com a imagem centrada e sem zoom. */
export function centredView(img: Box, box: Box): View {
  const s = baseScale(img, box);
  return { zoom: 1, x: (box.width - img.width * s) / 2, y: (box.height - img.height * s) / 2 };
}

/**
 * O retangulo da imagem original que fica dentro da moldura, e o tamanho do
 * resultado: o lado maior tem `size` pixeis, sem nunca ampliar a foto.
 */
export function cropArea(
  img: Box,
  box: Box,
  view: View,
  size: number,
): { sx: number; sy: number; sw: number; sh: number; width: number; height: number } {
  const s = baseScale(img, box) * view.zoom;
  const sw = box.width / s;
  const sh = box.height / s;
  const long = Math.min(size, Math.max(sw, sh));
  const k = long / Math.max(box.width, box.height);
  return {
    sx: (0 - view.x) / s,
    sy: (0 - view.y) / s,
    sw,
    sh,
    width: Math.max(1, Math.round(box.width * k)),
    height: Math.max(1, Math.round(box.height * k)),
  };
}

export type ImageCropperProps = {
  /** A foto a recortar: um ficheiro escolhido ou o URL de uma ja guardada. */
  source: File | string;
  /** Largura / altura do recorte (1 = quadrado, 16 / 9 = largo). */
  aspect?: number;
  /** Lado maior do recorte enviado, em pixeis (nunca amplia a foto). */
  size?: number;
  /** Lado maior da moldura no ecra, em pixeis. */
  frame?: number;
  /** Recebe o recorte (WebP, ou PNG se o browser nao fizer WebP). */
  onCrop: (image: Blob) => Promise<void> | void;
  onCancel: () => void;
  /** Se existir, aparece "Usar sem recortar" e recebe a foto tal como veio. */
  onUseOriginal?: (() => void) | undefined;
  saveLabel?: string;
};

/**
 * Enquadrar uma foto: arrastar para escolher o centro, deslizar para aproximar.
 * O recorte e feito no browser; o servidor volta a codificar e tira os metadados.
 */
export function ImageCropper({
  source,
  aspect = 1,
  size = 512,
  frame = 256,
  onCrop,
  onCancel,
  onUseOriginal,
  saveLabel = 'Guardar foto',
}: ImageCropperProps) {
  const outer = useRef<HTMLDivElement>(null);
  // Num ecra estreito a moldura encolhe para caber na coluna.
  const [fit, setFit] = useState(frame);
  useLayoutEffect(() => {
    const available = outer.current?.clientWidth ?? 0;
    const widest = aspect >= 1 ? frame : frame * aspect;
    setFit(available > 0 && available < widest ? Math.floor(frame * (available / widest)) : frame);
  }, [aspect, frame]);
  const box = frameSize(aspect, fit);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [view, setView] = useState<View>({ zoom: 1, x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const local = typeof source === 'string' ? null : URL.createObjectURL(source);
    const src = local ?? (source as string);
    const image = new Image();
    let live = true;
    image.onload = () => {
      if (!live) return;
      const natural = { width: image.naturalWidth, height: image.naturalHeight };
      setImg(image);
      setUrl(src);
      setView(centredView(natural, box));
    };
    image.onerror = () => {
      if (live) setError('Não foi possível abrir esta imagem.');
    };
    image.src = src;
    return () => {
      live = false;
      if (local) URL.revokeObjectURL(local);
    };
    // A moldura so muda com o formato; recomeca se a foto ou o formato mudarem.
  }, [source, aspect, fit]);

  const natural = img ? { width: img.naturalWidth, height: img.naturalHeight } : null;

  function zoomTo(zoom: number) {
    if (!natural) return;
    // Aproxima a volta do centro da moldura.
    const ratio = zoom / view.zoom;
    const cx = box.width / 2;
    const cy = box.height / 2;
    setView(
      clampView(natural, box, {
        zoom,
        x: cx - (cx - view.x) * ratio,
        y: cy - (cy - view.y) * ratio,
      }),
    );
  }

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY };
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (!natural || !drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    setView(clampView(natural, box, { ...view, x: view.x + dx, y: view.y + dy }));
  }

  async function save() {
    if (!img || !natural) return;
    const area = cropArea(natural, box, view, size);
    const canvas = document.createElement('canvas');
    canvas.width = area.width;
    canvas.height = area.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, area.sx, area.sy, area.sw, area.sh, 0, 0, area.width, area.height);
    let blob: Blob | null = null;
    try {
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/webp', 0.92),
      );
    } catch {
      blob = null;
    }
    if (!blob) {
      setError('O browser não conseguiu preparar a imagem.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onCrop(blob);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível guardar a foto.');
    } finally {
      setSaving(false);
    }
  }

  const s = natural ? baseScale(natural, box) * view.zoom : 1;

  return (
    <div ref={outer} className="flex w-full flex-col items-start gap-3" data-testid="image-cropper">
      <div
        className="relative max-w-full cursor-move touch-none overflow-hidden border-2 border-gu-ink bg-gu-raised"
        style={{ width: box.width, height: box.height }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
        data-testid="photo-crop-frame"
      >
        {natural && url ? (
          <img
            src={url}
            alt="Pré-visualização da foto"
            draggable={false}
            className="absolute left-0 top-0 max-w-none select-none"
            style={{
              width: natural.width * s,
              height: natural.height * s,
              transform: `translate(${String(view.x)}px, ${String(view.y)}px)`,
            }}
          />
        ) : null}
      </div>
      <label
        className="flex max-w-full flex-col gap-1 font-sub text-label font-bold uppercase text-gu-ink-muted"
        style={{ width: Math.min(box.width, 256) }}
      >
        Aproximar
        <input
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={view.zoom}
          disabled={!natural}
          onChange={(e) => zoomTo(Number(e.target.value))}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => void save()} disabled={saving || !natural}>
          {saving ? 'A guardar...' : saveLabel}
        </Button>
        {onUseOriginal ? (
          <Button type="button" variant="secondary" onClick={onUseOriginal} disabled={saving}>
            Usar sem recortar
          </Button>
        ) : null}
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-caption text-gu-accent">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export type PhotoCropperProps = {
  /** Recebe o recorte quadrado (WebP, ou PNG se o browser nao fizer WebP). */
  onSave: (image: Blob) => Promise<void> | void;
  /** Lado do quadrado enviado, em pixeis. */
  size?: number;
  /** Texto do botao que abre a escolha do ficheiro. */
  chooseLabel?: string;
  className?: string;
};

/**
 * Escolher uma foto e recorta-la em quadrado (foto de perfil). No telemovel o
 * seletor abre a camara ou a galeria.
 */
export function PhotoCropper({
  onSave,
  size = 512,
  chooseLabel = 'Escolher foto',
  className,
}: PhotoCropperProps) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  function pick(chosen: File | undefined) {
    setError(null);
    if (!chosen) return;
    if (!ACCEPT.split(',').includes(chosen.type)) {
      setError('Escolhe uma imagem JPEG, PNG ou WebP.');
      return;
    }
    setFile(chosen);
  }

  return (
    <div className={className}>
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {file ? (
        <ImageCropper
          source={file}
          size={size}
          onCrop={async (image) => {
            await onSave(image);
            setFile(null);
          }}
          onCancel={() => setFile(null)}
        />
      ) : (
        <Button type="button" variant="secondary" onClick={() => input.current?.click()}>
          {chooseLabel}
        </Button>
      )}
      {error ? (
        <p role="alert" className="mt-2 text-caption text-gu-accent">
          {error}
        </p>
      ) : null}
    </div>
  );
}
